import {useDealers} from '@api'
import {NavigationPathKey, TranslationKey} from '@enums'
import {GridColDef, GridColumnVisibilityModel} from '@mui/x-data-grid'
import {pageLinksAtom} from '@state'

import {useEffect, useMemo} from 'react'

import {activeCell, isCompanyUser, renderClickableCell, useViewNavigate} from 'common'
import {useSetAtom} from 'jotai'
import {useTranslation} from 'react-i18next'

export interface DealerTableData {
  name: string
  address: string
  city: string
  dealerNo: string
  companyNo: string
  active: boolean
}

export function useDealerSearchModel() {
  const setTitleLinks = useSetAtom(pageLinksAtom)
  const {t} = useTranslation()
  const navigate = useViewNavigate()

  const {data} = useDealers()

  const rows = useMemo(() => {
    if (!data) return []

    return data.map(item => ({
      name: item.name,
      address: item.address,
      city: item.city,
      companyNo: item.companyNo,
      dealerNo: item.dealerNo,
      active: item.active,
      id: item.id,
    }))
  }, [data])

  useEffect(
    () =>
      setTitleLinks([
        {title: t(TranslationKey['Admin panel']), link: NavigationPathKey.ADMIN_PANEL},
        {title: t(TranslationKey.Resources), link: NavigationPathKey.RESOURCES_DASHBOARD},
        {
          title: t(TranslationKey['Dealer search']),
        },
      ]),
    [],
  )

  /**
   * Dealer table columns configuration
   */
  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t(TranslationKey.Name),
      flex: 1,
      hideable: false,
      renderCell: params =>
        renderClickableCell(params, () => navigate(`${NavigationPathKey.DEALER_VIEW}/${params.row.id}`)),
    },
    {field: 'dealerNo', headerName: t(TranslationKey['Dealer number']), flex: 1},
    {field: 'address', headerName: t(TranslationKey.Address), flex: 1.2},
    {field: 'city', headerName: t(TranslationKey.City), flex: 1},
    {field: 'companyNo', headerName: t(TranslationKey['Company number']), flex: 1},
    activeCell(t),
  ]

  /**
   * Dealer table visibility model configuration
   */
  const visibilityModel: GridColumnVisibilityModel = {
    dealerNo: false,
    address: false,
    companyNo: false,
  }

  /**
   * Create button should be available only for Administrator users
   */
  const showCreateButton = !isCompanyUser()

  return {navigate, visibilityModel, t, showCreateButton, rows, columns}
}
